import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import type { DragEndEvent } from "@dnd-kit/core/dist/types/events"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import Table, { ColumnsType } from "antd/lib/table"
import React from "react"
import { ExpandableListItemIcon } from "~Components/icons/ExpandableListItemIcon"
import { DraggableTableDataSourceType } from "~Shared/hooks/table.hooks"
import { getOverlayTableDataSource } from "~Shared/utils/table.utils"

interface DraggableTableWrapperProps<T> {
    onDragEnd: (event: DragEndEvent) => void
    onDragStart: (event: DragEndEvent) => void
    columns: ColumnsType
    children: JSX.Element
    dataSource: Array<DraggableTableDataSourceType<T>>
    activeId: string | null
}

export const DraggableTableWrapper = <T,>({ onDragEnd, children, dataSource, onDragStart, activeId, columns }: DraggableTableWrapperProps<T>) => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
                distance: 1,
            },
        }),
    )
    const tableClassName = `draggable-table ${activeId ? "isDragging" : ""}`

    return (
        <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd} onDragStart={onDragStart}>
            <SortableContext items={dataSource.map((i: DraggableTableDataSourceType<T>) => i.key)} strategy={verticalListSortingStrategy}>
                <div className={tableClassName}>{children}</div>
            </SortableContext>
            <DragOverlay>
                <Table<DraggableTableDataSourceType<T>>
                    columns={columns}
                    showHeader={false}
                    dataSource={getOverlayTableDataSource(activeId, dataSource)}
                    pagination={false}
                    expandable={{
                        expandIconColumnIndex: 1,
                        expandIcon: ({ expanded, onExpand, record }) => {
                            if (!record?.children || record?.children?.length === 0) {
                                return <></>
                            }
                            return (
                                <button className="outline-none px-10" onClick={(e) => onExpand(record, e)}>
                                    <ExpandableListItemIcon expanded={expanded} />
                                </button>
                            )
                        },
                    }}
                />
            </DragOverlay>
        </DndContext>
    )
}
